import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Permission, Role } from '@pizza-roma/shared';
import { api } from './api';

export interface Me {
  user: { id: string; email: string; displayName: string; isPlatformAdmin: boolean } | null;
  restaurant?: { id: string; slug: string; name: string } | null;
  roles?: Role[];
  permissions?: Permission[];
  memberships?: { restaurantId: string; slug: string; name: string; role: Role; status: string }[];
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<Me>;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  setMe: (m: Me) => void;
  can: (p: Permission) => boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    const m = await api.get<Me>('/api/auth/me').catch(() => ({ user: null }) as Me);
    setMe(m);
    setLoading(false);
    return m;
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const login = async (email: string, password: string) => {
    const m = await api.post<Me>('/api/auth/login', { email, password });
    setMe(m);
    return m;
  };
  const logout = async () => {
    await api.post('/api/auth/logout');
    setMe({ user: null });
  };
  const can = (p: Permission) => !!me?.permissions?.includes(p);
  return <Ctx.Provider value={{ me, loading, refresh, login, logout, setMe, can }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('AuthProvider missing');
  return c;
}

/** Where a user lands after login, from their roles. */
export function homeFor(m: Me): string {
  const roles = m.roles ?? [];
  if (roles.includes('OWNER') || roles.includes('MANAGER') || roles.includes('STAFF')) return '/restaurant/dashboard';
  if (roles.includes('KITCHEN')) return '/restaurant/kitchen';
  if (roles.includes('DRIVER')) return '/driver';
  if (m.user?.isPlatformAdmin) return '/platform/restaurants';
  const customerOf = m.memberships?.find((x) => x.role === 'CUSTOMER');
  return customerOf ? `/r/${customerOf.slug}/account` : '/';
}
